import { useState } from 'react';
import { sortThread } from '../lib/commentThread.ts';
import { formatDateOnly } from '../lib/expiry.ts';
import type { SubmissionComment } from '../lib/types.ts';
import { Btn } from './Btn.tsx';
import { ErrorMessage } from './ErrorMessage.tsx';

/** Save is offered only for non-blank text that actually changes something. */
export function canSaveCommentEdit(original: string, next: string): boolean {
  return next.trim().length > 0 && next !== original;
}

/**
 * A reviewer comment Notify has not sent yet (published_at null) can have its
 * text fixed. A file-only comment has no text to fix. The vendor payload holds
 * published admin comments only, so this is never true there.
 */
function isEditableDraft(comment: SubmissionComment): boolean {
  return comment.author_type === 'admin' && comment.published_at === null && !!comment.body;
}

function EditBox({
  original,
  onSave,
  onCancel,
}: {
  original: string;
  onSave: (body: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [text, setText] = useState(original);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await onSave(text);
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2">
      <textarea
        aria-label="Edit comment"
        className="w-full rounded-md border border-brand-orange-border px-3 py-2 text-sm leading-5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-orange-border"
        rows={Math.min(8, Math.max(3, text.split('\n').length))}
        maxLength={4000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={saving}
      />
      <ErrorMessage error={error} />
      <div className="flex gap-2">
        <Btn
          size="sm"
          loading={saving}
          disabled={!canSaveCommentEdit(original, text)}
          onClick={() => void save()}
        >
          Save
        </Btn>
        <Btn size="sm" variant="ghost" disabled={saving} onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function Bubble({
  comment,
  edit,
}: {
  comment: SubmissionComment;
  /** Present only on an editable draft in the admin popup. */
  edit?:
    | {
        editing: boolean;
        onStart: () => void;
        onSave: (body: string) => Promise<unknown>;
        onCancel: () => void;
      }
    | undefined;
}) {
  const isAdmin = comment.author_type === 'admin';
  // Absent on an older API payload; a reply that removed nothing carries [].
  const removed = comment.removed_files ?? [];
  const editing = edit?.editing === true;
  return (
    <div className={`${editing ? 'w-[80%]' : 'max-w-[80%]'} ${isAdmin ? '' : 'self-end'}`}>
      <div className={`mb-1.5 flex items-center gap-2 ${isAdmin ? '' : 'justify-end'}`}>
        {isAdmin && <span className="rounded bg-brand-orange px-2 py-0.5 text-[11px] font-bold text-white">Reviewer</span>}
        <span className="text-[11px] text-slate-400">{formatTime(comment.created_at)}</span>
        {edit && !editing && (
          <button
            type="button"
            className="text-[11px] font-semibold text-[#4D148C] hover:underline"
            onClick={edit.onStart}
          >
            Edit
          </button>
        )}
        {!isAdmin && (
          <span className="rounded px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: 'var(--brand-primary)' }}>
            Vendor
          </span>
        )}
      </div>
      {editing && comment.body ? (
        <EditBox original={comment.body} onSave={edit!.onSave} onCancel={edit!.onCancel} />
      ) : comment.body && (
        <div
          // Reviewer orange is a tint with dark text: white on #FF6600 is only ~2.9:1.
          className={`px-3.5 py-2.5 text-sm leading-5 ${isAdmin ? 'rounded-[4px_14px_14px_14px] border border-brand-orange-border bg-brand-orange-tint text-slate-900' : 'rounded-[14px_4px_14px_14px] text-white'}`}
          style={isAdmin ? undefined : { background: 'var(--brand-primary)' }}
        >
          <div className="whitespace-pre-wrap">{comment.body}</div>
        </div>
      )}
      {comment.attachments.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {comment.attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4D148C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
              </svg>
              <div className="min-w-0 flex-1">
                {a.attachment_url ? (
                  <a href={a.attachment_url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs font-semibold text-[#4D148C] hover:underline">{a.attachment_name}</a>
                ) : (
                  <span className="block truncate text-xs font-semibold text-slate-600">{a.attachment_name}</span>
                )}
                <span className="text-[11px] text-slate-400">
                  {a.is_answer ? 'Document' : 'Reference'}
                  {a.attachment_expires_at ? ` · expires ${formatDateOnly(a.attachment_expires_at)}` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {removed.map((f) => (
            // No link: a removed file is history, reachable from version history only.
            <div key={f.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
              </svg>
              <div className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold line-through text-slate-500">{f.file_name}</span>
                <span className="text-[11px] text-slate-400">Removed</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommentThread({
  comments,
  onEditBody,
  initialEditingId = null,
}: {
  comments: SubmissionComment[];
  /** Admin popup only: saves new text for an unsent reviewer comment. Rejects on failure. */
  onEditBody?: ((commentId: string, body: string) => Promise<unknown>) | undefined;
  /** Test seam (no DOM harness): start with this comment's edit box open. */
  initialEditingId?: string | null;
}) {
  const [editingId, setEditingId] = useState<string | null>(initialEditingId);
  const sorted = sortThread(comments);
  if (sorted.length === 0) return <p className="text-xs text-slate-400">No messages yet.</p>;
  return (
    <div className="flex flex-col gap-4">
      {sorted.map((c) => (
        <Bubble
          key={c.id}
          comment={c}
          edit={
            onEditBody && isEditableDraft(c)
              ? {
                  editing: editingId === c.id,
                  onStart: () => setEditingId(c.id),
                  onCancel: () => setEditingId(null),
                  onSave: async (body) => {
                    await onEditBody(c.id, body);
                    setEditingId(null);
                  },
                }
              : undefined
          }
        />
      ))}
    </div>
  );
}
